<script lang="ts" setup>

  import { format, parse } from 'date-fns'
  import { fr } from 'date-fns/locale'

  import { BngHoles } from '~/components/golf'
  import { DATE_FORMAT } from '~/core/constants'
  import type { Booking, CartSoldProduct } from '~/core/types/order'

  type Props = {
    booking: Booking | CartSoldProduct
    light?: boolean
    slim?: boolean
  }

  const props = defineProps<Props>()

  const { t } = useI18n()

  // eslint-disable-next-line @stylistic/quotes
  const FORMAT = "dd MMMM yyyy 'à' HH:mm"
  const date = computed(() => 'course' in props.booking
    ? format(parse(props.booking.date, DATE_FORMAT, new Date()), FORMAT, {
      locale: fr,
    })
    : null)
</script>
<template>
  <div
    class="c-product-booking"
    :class="{ light: light, slim: slim, 'is-product': !('course' in props.booking) }"
  >
    <div
      class="picture"
      :class="{ large: !('course' in props.booking) }"
      :style="{
        'background-image': `url('${
          'course' in props.booking ? props.booking.course.golf.thumb : props.booking.product_thumb
        }')`,
      }"
    />
    <div class="top-card">
      <span>{{ "course" in props.booking ? "Green Fee" : props.booking.category }}</span>
    </div>
    <div class="content">
      <div class="description">
        <div class="name-date">
          <div class="name">
            <SvgIcon
              icon="location"
              :width="10"
              :height="16"
            />
            <span>{{ "golf_name" in props.booking ? props.booking.golf_name : props.booking.course?.golf.name }}</span>
          </div>
          <div class="datetime">
            <span v-if="'course' in props.booking">{{ date }}</span>
            <span v-else>{{ props.booking.name }}</span>
          </div>
        </div>
        <div
          v-if="'course' in props.booking"
          class="specs"
        >
          <div class="player-number">
            {{ props.booking.players.length }}&nbsp;{{
              t("shopping.player", props.booking.players.length)
            }}
          </div>
          <div class="holes-list">
            <BngHoles :model-value="props.booking.course.holes" />
            <div class="course">
              <span>{{ props.booking.course.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style  scoped>

.c-product-booking {
  position: relative;
  display: flex;
  flex: 1 0 auto;
  flex-direction: row;
  padding-top: 1em;

  .top-card {
    position: absolute;
    top: 0;
    display: flex;
    align-items: center;
    padding: 0.125em;
    border-radius: 4px;
    background-color: var(--color-grey-dark);

    span {
      color: white;
      font-family: Open-Sans, sans-serif;
      font-size: 0.875em;
      font-weight: 600;
    }
  }

  .picture {
    position: relative;
    width: 120px;
    min-width: 120px;
    height: 120px;
    flex: 0 0 auto;
    border-radius: 4px;
    background-color: var(--color-secondary);
    background-position: center;
    background-size: cover;

    &.large {
      width: 280px;
    }
  }

  .content {
    display: flex;
    overflow: hidden;
    flex: 1 1 auto;
    flex-direction: row;
    justify-content: space-between;
    padding-left: 1.25em;

    .description {
      display: flex;
      overflow: hidden;
      flex: 1 1 auto;
      flex-direction: column;

      .name-date {
        display: flex;
        flex-direction: column;

        .name {
          display: flex;
          flex-flow: row nowrap;

          .c-icon {
            display: flex;
            margin: 0.25em 0.25em auto;
            color: var(--color-secondary);
            opacity: 0.8;
          }

          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1em;
            font-weight: 600;
            opacity: 0.8;
          }
        }

        .datetime {
          margin-top: 0.375em;

          span {
            color: var(--color-secondary);
            font-family: Bariol, sans-serif;
            font-size: 1.25em;
            font-weight: bold;
          }
        }
      }

      .specs {
        display: flex;
        flex-direction: column;
        margin-top: 0.75rem;

        .player-number {
          margin-right: 2rem;
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1em;
          font-weight: bold;
          letter-spacing: 0.22px;
          line-height: 1.27;
        }

        .holes-list {
          display: flex;
          overflow: hidden;
          flex: 1 1 auto;
          flex-direction: row;
          margin-top: 0.75em;
          opacity: 0.6;

          .course {
            overflow: hidden;
            flex: 1 1 auto;
            margin-left: 0.5em;
            opacity: 0.6;

            span {
              display: inline-block;
              overflow: hidden;
              max-width: 100%;
              color: var(--color-secondary);
              font-family: Open-Sans, sans-serif;
              font-size: 0.875em;
              font-weight: normal;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
      }
    }
  }

  &.light {
    padding: 0;

    .picture {
      display: none;
    }

    .content {
      padding: 0;

      .description {
        display: flex;
        flex-direction: column;

        .name-date {
          display: flex;

          .name {
            margin-left: 5em;
          }
        }

        .specs {
          .player-number {
            position: absolute;
            top: 2em;
            right: 0;
            margin: 0;
          }
        }

        span {
          display: flex;
          flex-direction: column;

          .name {
            margin: auto 0;
          }

          .datetime {
            display: flex;
            margin: 0.5em 0;
          }
        }
      }
    }

    &.is-product {
      .content .description .name-date {
        flex-direction: column;

        .name {
          margin-top: 0.25em;
          margin-left: 10em;
        }

        .datetime {
          margin: 1.25em 0 0;
        }
      }
    }
  }

  @media screen and (width >= 1024px) {
    .top-card {
      padding: 0.25em;

      span {
        font-size: 1.125em;
      }
    }

    .picture {
      width: 160px;
      height: 160px;
    }

    .content {
      padding: 1.25em;

      .description {
        .name-date {
          .name {
            span {
              font-size: 1.125em;
            }
          }

          .datetime {
            margin-top: 1em;
            line-height: 1.25;

            span {
              font-size: 1.625em;
            }
          }
        }

        .specs {
          flex-direction: row;
          align-items: center;
          margin-top: 1.125em;

          .player-number {
            font-size: 1.125em;
          }

          .holes-list {
            margin: 0;

            .course {
              display: flex;
              align-items: center;

              span {
                font-size: 1em;
              }
            }
          }
        }
      }
    }

    &.light {
      .content {
        .description {
          .name-date {
            flex-direction: row;

            .name {
              margin: auto 0 auto 6.5em;
            }

            .datetime {
              margin: auto 2em;
            }
          }

          .specs {
            .player-number {
              position: relative;
              top: 0;
              margin-right: 0.5em;
            }
          }
        }
      }
    }

    &.slim {
      .content {
        padding: 0.5em 1em;

        .description {
          .name-date {
            .name {
              span {
                font-size: 1em;
              }
            }

            .datetime {
              margin-top: 1em;
              line-height: 1;

              span {
                font-size: 1.5em;
              }
            }
          }

          .specs {
            flex-direction: row;
            align-items: center;
            margin-top: 1.125em;

            .player-number {
              font-size: 1.125em;
            }

            .holes-list {
              margin: 0;

              .course {
                display: flex;
                align-items: center;

                span {
                  font-size: 1em;
                }
              }
            }
          }
        }
      }

      .picture {
        width: 120px;
        height: 120px;
      }
    }
  }
}
</style>
